<script setup lang="ts">
import { computed, ref } from 'vue';

import type { VFormRef } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useQueryClient } from '@tanstack/vue-query';

import type { ITrackerTrafficSourceComposableOptions, ITrackerTrafficSourceForm } from '../../types';
import type { IChangeTrafficSourceEmits, IChangeTrafficSourceProps } from './types';
import { useTrackerTrafficSource } from '../../composables';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS } from '../../constants';
import { useChangeTrackerTrafficSourceMutation } from '../../mutations';

const {
  affId,
  trackerId,
  trafficSourceId,
  trackersListQueryKey,
} = defineProps<IChangeTrafficSourceProps>();

const emit = defineEmits<IChangeTrafficSourceEmits>();

const visible = defineModel<boolean>('visible');

const form = ref<VFormRef>();

const queryClient = useQueryClient();

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
  clearAllErrors,
} = useChangeTrackerTrafficSourceMutation();

const trafficSourceFormOptions = computed<ITrackerTrafficSourceComposableOptions>(() => ({
  affiliateId: affId,
  withoutDefault: true,
  enableQuery: visible.value,
  trafficSourceValue: trafficSourceId,
}));

const {
  formTrafficSourceSchema,
  formGroups,
  isCustomTrafficSource,
  showTrafficSourceWarning,
  onChange,
} = useTrackerTrafficSource(trafficSourceFormOptions);

async function onSubmit({ traffic_source_id }: ITrackerTrafficSourceForm): Promise<void> {
  if (!traffic_source_id || !trackerId)
    return;

  await mutateAsync({
    affId,
    trackerId,
    tracker: {
      traffic_source_id,
    },
  });

  if (trackersListQueryKey) {
    void queryClient.invalidateQueries({ queryKey: [trackersListQueryKey] });
  }

  close();
}

function onFormSubmit(): void {
  form.value?.submit();
}

function onHide(): void {
  showTrafficSourceWarning.value = false;
  clearAllErrors();
  emit('hide');
}

function close(): void {
  visible.value = false;
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRAFFIC_SOURCE_TITLE')"
    @hide="onHide"
  >
    <VForm
      ref="form"
      :schema="formTrafficSourceSchema"
      :groups="formGroups"
      :is-loading="isLoading"
      :custom-errors="errors"
      :readonly="isCustomTrafficSource"
      @change="onChange"
      @submit="onSubmit"
      @interacted="clearError"
    />
    <template #footer>
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRAFFIC_SOURCE_CANCEL')"
        outlined
        @click="close"
      />
      <VButton
        v-if="!isCustomTrafficSource"
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRAFFIC_SOURCE_SAVE')"
        :loading="isLoading"
        @click="onFormSubmit"
      />
    </template>
  </VDialog>
</template>
